if (!customElements.get('video-scroll-hero')) {
  class VideoScrollHero extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.vh-hero__stage');
      this.video = this.querySelector('.vh-hero__video');
      this.poster = this.querySelector('.vh-hero__poster');
      this.content = this.querySelector('.vh-hero__content');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isSmallScreen = window.matchMedia('(max-width: 749px)').matches;

      if (!this.video || reduceMotion || isSmallScreen) {
        // Static fallback: no scrubbing, content always fully visible.
        return;
      }

      this.ready = false;
      this.rafId = null;
      this.progress = 0;
      this.pendingTime = null;
      this.hasPainted = false;

      // A scrubbed video is never "played" — make sure nothing else tries to.
      this.video.pause();
      this.video.muted = true;
      this.video.playsInline = true;
      if (!this.video.hasAttribute('preload')) this.video.preload = 'auto';

      this.onLoadedMetadata = () => {
        this.ready = true;
        this.update();
      };
      this.video.addEventListener('loadedmetadata', this.onLoadedMetadata);
      if (this.video.readyState >= 1) this.onLoadedMetadata();

      // Hold the poster until a real video frame exists, otherwise the first
      // scroll happens against an undecoded (blank) video surface.
      this.onFirstPaint = () => {
        if (this.hasPainted) return;
        this.hasPainted = true;
        if (this.poster) this.poster.style.opacity = '0';
      };
      this.video.addEventListener('loadeddata', this.onFirstPaint);
      this.video.addEventListener('seeked', this.onFirstPaint);
      if (this.video.readyState >= 2) this.onFirstPaint();

      // One seek may be in flight at a time. When it lands, immediately chase
      // whatever the newest scroll position asked for.
      this.onSeeked = () => {
        const next = this.pendingTime;
        this.pendingTime = null;
        if (next === null) return;
        if (Math.abs(this.video.currentTime - next) > 0.04) this.seekTo(next);
      };
      this.video.addEventListener('seeked', this.onSeeked);

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.video) {
        this.video.removeEventListener('loadedmetadata', this.onLoadedMetadata);
        this.video.removeEventListener('loadeddata', this.onFirstPaint);
        this.video.removeEventListener('seeked', this.onFirstPaint);
        this.video.removeEventListener('seeked', this.onSeeked);
      }
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    seekTo(time) {
      // fastSeek lands on the nearest keyframe instead of decoding forward to
      // an exact frame. For a scrub that is the right trade — it is what keeps
      // the motion continuous. Chrome has no fastSeek, so it falls through.
      if (typeof this.video.fastSeek === 'function') {
        try {
          this.video.fastSeek(time);
          return;
        } catch (e) {
          /* fall through to currentTime */
        }
      }
      this.video.currentTime = time;
    }

    update() {
      const rect = this.getBoundingClientRect();
      const scrollable = this.offsetHeight - window.innerHeight;
      let progress = scrollable > 0 ? -rect.top / scrollable : 0;
      this.progress = Math.min(1, Math.max(0, progress));

      // The overlay fade is pure compositing and can run every frame.
      if (this.content) {
        const fade = Math.min(1, this.progress / 0.5);
        this.content.style.opacity = String(1 - fade);
        this.content.style.transform = 'translate3d(0,' + (-40 * fade) + 'px,0)';
      }

      this.scrub();
    }

    scrub() {
      if (!this.ready || !this.video.duration || !isFinite(this.video.duration)) return;

      const target = this.progress * this.video.duration;

      // THE stutter fix. The old code assigned currentTime on every animation
      // frame during a scroll. Each assignment cancels the seek already being
      // decoded and starts another, so under a continuous scroll the decoder
      // is restarted ~60x a second and never finishes one — which is why it
      // looked like the video froze for a beat, then jumped. Now only one seek
      // is ever outstanding; newer positions queue and are picked up in
      // 'seeked'.
      if (this.video.seeking) {
        this.pendingTime = target;
        return;
      }

      if (Math.abs(this.video.currentTime - target) < 0.033) return;

      // Don't seek past what has actually been buffered — an unbuffered seek
      // blocks on the network and produces the same visible stall.
      const seekable = this.video.seekable;
      if (seekable && seekable.length) {
        const end = seekable.end(seekable.length - 1);
        if (target > end) {
          this.pendingTime = target;
          this.seekTo(Math.max(0, end - 0.05));
          return;
        }
      }

      this.seekTo(target);
    }
  }

  customElements.define('video-scroll-hero', VideoScrollHero);
}
